import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';

// import WeirdStars from '@/shared/assets/weird-stars.svg?react';
import { Gradients } from '@/shared/components/theme/gradient';
import { cn } from '@/shared/utils/cn';

const cardVariants = cva(
  'rounded-xl border bg-card text-card-foreground shadow flex flex-col',
  {
    variants: {
      variant: {
        default: '',
        information: `w-full max-w-[400px] border-white/[0.16] border bg-transparent bg-gradient-to-b ${Gradients.transparentWhiteGradient} text-white`,
        informationAlt: 'gap-4 p-6 bg-dark-container border-none h-[300px]',
        styled:
          'border border-white/15 bg-transparent-white-gradient/5 backdrop-blur-[200px] drop-shadow-[0_10px_10px_rgba(0,0,0,0.1)] drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)] drop-shadow-[0_1px_0px_rgba(0,0,0,0.05)] shadow-[inset_0_8px_50px_rgba(255,255,255,0.02)]',
        form: 'border border-white/15 bg-transparent-white-gradient/5 backdrop-blur-[200px] drop-shadow-[0_10px_10px_rgba(0,0,0,0.1)] drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)] drop-shadow-[0_1px_0px_rgba(0,0,0,0.05)] shadow-[inset_0_8px_50px_rgba(255,255,255,0.02)] max-w-[800px] px-4 py-8 md:px-11 md:py-14 w-full',
        chart: `w-full border-white/[0.16] border bg-transparent shadow-none text-white`,
      },
    },
    compoundVariants: [
      {
        variant: 'information',
        className: `hover:bg-[#D9D9D9]/10 hover:bg-blend-overlay`,
      },
    ],
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  wrapWithStars?: boolean;
}

// Create a context to share card variant information,
// which we use to determine the card header and title styles
const CardContext = React.createContext<{
  variant?: CardProps['variant'];
}>({ variant: 'default' });

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, wrapWithStars, children, ...props }, ref) => (
    <CardContext.Provider value={{ variant }}>
      <div
        ref={ref}
        className={cn(
          cardVariants({ variant, className }),
          wrapWithStars && 'relative overflow-visible',
        )}
        {...props}
      >
        {/* {wrapWithStars && (
          <>
            <div className="absolute -left-20 -top-20">
              <WeirdStars className="size-full rotate-180" />
            </div>

            <div className="absolute -bottom-20 -right-20">
              <WeirdStars className="size-full" />
            </div>
          </>
        )} */}
        {children}
      </div>
    </CardContext.Provider>
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(CardContext);

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 p-6',
        variant === 'form' && 'mb-12 p-0',
        variant === 'styled' && 'rounded-t-lg',
        variant === 'informationAlt' && 'space-y-0 p-0',
        className,
      )}
      {...props}
    />
  );
});
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(CardContext);

  return (
    <div
      ref={ref}
      className={cn(
        'font-semibold leading-none tracking-tight',
        variant === 'form' &&
          'text-center text-xl font-medium -tracking-wider md:text-3xl lg:text-4xl lg:leading-[3.6rem]',
        className,
      )}
      {...props}
    />
  );
});
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('mt-1 text-4xs text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(CardContext);

  return (
    <div
      ref={ref}
      className={cn(
        'p-6 pt-0',
        (variant === 'form' || variant === 'informationAlt') && 'p-0',
        variant === 'informationAlt' && 'h-full',
        className,
      )}
      {...props}
    />
  );
});
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(CardContext);

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center p-6 pt-0',
        variant === 'form' && 'mt-16 justify-center gap-10 p-0',
        className,
      )}
      {...props}
    />
  );
});
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
